import React from 'react';
import { Table, Transfer } from 'antd';
import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { CalendarService } from '../../services/calendar.api.client';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfig } from '../../contexts/ConfigContext';
import { useModalCalendar } from '../../contexts/calendar/ModalCalendarContext.context';
import { Calendar } from '../../models/calendar/Calendar.model';
import { useCalendars } from '../../contexts/calendar/CalendarsContext.context';

function findCalendarById(calendars: Calendar[], id: string) {
    return calendars.find(c => c.uid === id || c.id === id);
}

type TransferItem = GetProp<TransferProps, 'dataSource'>[number];
type TableRowSelection<T extends object> = TableProps<T>['rowSelection'];

export interface DataType {
    label: string
    key: string
    numericId?: number  // Supabase numeric user ID for junction table
}

export interface TableTransferProps extends TransferProps<TransferItem> {
  dataSource: DataType[];
  leftColumns: TableColumnsType<DataType>;
  rightColumns: TableColumnsType<DataType>;
  calendar?: Calendar;
  onSearch?: (direction: string, value: string) => void;
}

// Customize Table Transfer
const TableTransfer: React.FC<TableTransferProps> = (props) => {
  const { leftColumns, rightColumns, calendar: calendarProp, onSearch, ...restProps } = props;
  const { options } = useModalCalendar();
  const { company } = useCompany();
  const { config } = useConfig();
  const { calendars, setCalendars } = useCalendars();

  const handleChangeTransfer: TransferProps['onChange'] = (newTargetKeys) => {
    // Call parent onChange to update targetKeys state
    if (restProps.onChange) {
      restProps.onChange(newTargetKeys, 'right', []);
    }

    // Build enriched data from dataSource for the selected keys
    const dataSource = restProps.dataSource as DataType[];
    const selectedUsers = newTargetKeys
      .map(key => dataSource.find(u => u.key === key))
      .filter(Boolean) as DataType[];

    // Numeric IDs for Supabase calendar_users junction table
    const numericIds = selectedUsers
      .map(u => u.numericId)
      .filter((id): id is number => typeof id === 'number' && !isNaN(id));

    // Enriched objects for Firestore
    const usersEnriched = selectedUsers
      .filter(u => u.numericId != null)
      .map(u => ({ id: u.numericId, uid: u.key, name: u.label }));

    // Persist to backend
    const currentCalendar = calendarProp || findCalendarById(calendars, options.id);
    CalendarService.updateCalendar({
      users: numericIds,
      uid: currentCalendar?.uid || options.id,
      id: currentCalendar?.id || '',
      users_enriched: usersEnriched
    } as any, company.current, config.key).then(() => {
      const index = calendars.findIndex((item) => item.id == options.id || item.uid == options.id);
      if (index !== -1) {
        // Store enriched user objects in local state (consistent with Firestore)
        calendars[index].users = usersEnriched.map(u => ({ id: u.id!, uid: u.uid, name: u.name }));
        setCalendars([...calendars]);
      }
    });
  };

  return (
    <Transfer
      style={{ width: '100%' }}
      {...restProps}
      onChange={handleChangeTransfer}
      onSearch={onSearch}
    >
      {({
        direction,
        filteredItems,
        onItemSelect,
        onItemSelectAll,
        selectedKeys: listSelectedKeys,
        disabled: listDisabled,
      }) => {
        const columns = direction === 'left' ? leftColumns : rightColumns;
        const rowSelection: TableRowSelection<TransferItem> = {
          getCheckboxProps: () => ({ disabled: listDisabled }),
          onChange(selectedRowKeys) {
            onItemSelectAll(selectedRowKeys, 'replace');
          },
          selectedRowKeys: listSelectedKeys,
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
        };

        return (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredItems}
            pagination={{ pageSize: 4 }}
            size="small"
            style={{ pointerEvents: listDisabled ? 'none' : undefined }}
            onRow={({ key, disabled: itemDisabled }) => ({
              onClick: () => {
                if (itemDisabled || listDisabled) {
                  return;
                }
                onItemSelect(key, !listSelectedKeys.includes(key));
              },
            })}
          />
        );
      }}
    </Transfer>
  );
};

export default TableTransfer;